import { useLocation } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addFavourite, removeFavourite } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function SaveProjectButton({ slug }: { slug: string }) {
  const { user, setUser, signIn } = useAuth();
  const location = useLocation();
  const qc = useQueryClient();
  const saved = !!user?.favourites.includes(slug);

  const toggle = useMutation({
    mutationFn: () => (saved ? removeFavourite(slug) : addFavourite(slug)),
    onSuccess: (u) => {
      setUser(u);
      qc.invalidateQueries({ queryKey: ["favourites"] });
    },
  });

  const onClick = () => {
    if (!user) return signIn(location.pathname);
    toggle.mutate();
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={toggle.isPending}
      data-testid="project-save-button"
      aria-pressed={saved}
      className={`inline-flex items-center gap-2 border px-5 py-3 font-display text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-60 ${
        saved ? "border-gold bg-gold text-obsidian" : "border-white/25 text-zinc-200 hover:border-gold hover:text-gold"
      }`}
    >
      <Bookmark className={`h-4 w-4 ${saved ? "fill-current" : ""}`} />
      {saved ? "Saved" : user ? "Save project" : "Sign in to save"}
    </button>
  );
}
